import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendOrder,
	FrontendCompany,
	FrontendProductOrder,
} from "node-library/dist/portal";
import { OrderTableSortColumn, SortOrder } from "node-library/dist/paginations";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../../requestHelper/sendRequest";
import {
	AdminViewOrderStatus,
	FrontendContentPage,
	OrderStatus,
	OrderType,
	Severity,
	UserType,
} from "node-library/dist/constants";
import OrderDetailsContent from "../orderDetailsContent";
import {
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../../common-components/inputs";
import { Pagination } from "@mui/material";
import {
	getAdminStatusString,
	getAdminStatusStringByAdminStatus,
	getOrderSortString,
	getOrderTypeString,
	getStatusString,
} from "../../../../helpers/orderHelper";
import {
	BackgroundColor,
	NeutralColor,
	SecondaryColor,
} from "node-library/dist/colors";
import NewOrEditOrder from "../../newOrEditOrderPage";
import { SortArrowIcon } from "../../../../common-components/svgIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { YellowCrossIcon } from "../../../../common-components/toast/warnIcons";
import DiscardOrderConfirmModal from "../../../../common-components/modals/discardOrderConfirmModal";
import AdminClientOrderContent from "./adminClientOrderContent";
import AdminViewOrderReportPage from "./adminViewClientOrderReportPage";
import OrderHistoryPageSkeletonUI from "../skeletonUI/orderHistoryPageSkeleton";
import { checkUserTypeMeetsRequirement } from "../../../../helpers/userHelper";

interface Props {
	user: FrontendUserInfo | undefined;
	company: FrontendCompany | undefined;

	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function AdminViewOrdersPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [orders, setOrders] = useState<Array<FrontendProductOrder>>([]);

	const [selectedOrder, setSelectedOrder] = useState<FrontendProductOrder>();

	const [loading, setLoading] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<OrderTableSortColumn>(
		OrderTableSortColumn.INSERTED
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [statusFilterOption, setStatusFilterOption] = useState<
		AdminViewOrderStatus | ""
	>("");

	const [orderTypeOption, setOrderTypeOption] = useState<OrderType | "">("");

	//confirm modals
	const [discardOrderConfirmOpen, setDiscardOrderConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	const [isInAdminView, setIsInAdminView] = useState<boolean>(true);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}
	const replaceOrderById = (newOrder: FrontendProductOrder) => {
		setOrders((prevOrders) =>
			prevOrders.map((order) =>
				order.orderId === newOrder.orderId ? { ...order, ...newOrder } : order
			)
		);
	};

	async function getOrders(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/orders/get-company-submitted-orders",
				{
					searchText,
					sortBy,
					sortOrder,
					pageNumber: currentPagination,
					statusFilterOption,
				}
			);

			const orders: Array<FrontendProductOrder> = response?.data?.orders;
			const pageCount: number = response?.data?.totalPages;
			setOrders(orders);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function refreshSelectedOrder() {
		try {
			const response = await sendGETRequest(
				"/orders/admin-get-submitted-order",
				{
					orderId: selectedOrder?.orderId,
				}
			);

			const newOrder: FrontendProductOrder = response?.data?.order;
			replaceOrderById(newOrder);
			setSelectedOrder(newOrder);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	async function deleteOrder(order: FrontendProductOrder) {
		if (!isInAdminView) {
			return;
		}
		//send a request to delete the temp order in the backend
		try {
			const response = await sendPOSTRequest("/orders/delete-order", {
				orderId: order.orderId,
			});
			//go to the submitted order page
			openSnackbar(
				translation("OrderDiscardSuccess"),
				Severity.WARN,
				undefined,
				<YellowCrossIcon />
			);
			await goBackToViewOrdersPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	async function viewOrderCOC(order: FrontendProductOrder) {
		if (!isInAdminView) {
			return;
		}
		try {
			const response = await sendGETRequest("/orders/get-order-coc-url", {
				orderId: order.orderId,
			});
			window.open(response.data.signedURL, "_blank");
		} catch (error) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	const statusSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(AdminViewOrderStatus).map(
		(status: AdminViewOrderStatus) => {
			const name: string = getAdminStatusStringByAdminStatus(
				status,
				translation
			);
			return {
				key: status,
				name: name,
				description: "",
				value: status,
				children: (
					<div
						className={`center-text status-selection-radio-option typography-body-02 ${
							statusFilterOption === status
								? "status-selection-radio-option-selected"
								: ""
						}`}
					>
						{name}
					</div>
				),
			};
		}
	);
	statusSelectionOptions.unshift({
		key: "All",
		name: translation("All"),
		description: "",
		value: "",
		children: (
			<div
				className={`center-text status-selection-radio-option typography-body-02 ${
					statusFilterOption === ""
						? "status-selection-radio-option-selected"
						: ""
				}`}
			>
				{translation("All")}
			</div>
		),
	});
	const statusSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "800px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={statusSelectionOptions}
				selectedOption={statusFilterOption}
				onChange={(e) => {
					setStatusFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const orderTypeSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(OrderType).map((type: OrderType) => {
		const name: string = getOrderTypeString(type, translation);
		return {
			key: type,
			name: name,
			description: "",
			value: type,
			children: (
				<div
					className={`center-text order-type-selection-radio-option typography-body-01 ${
						orderTypeOption === type
							? "order-type-selection-radio-option-selected"
							: ""
					}`}
				>
					{name}
				</div>
			),
		};
	});
	orderTypeSelectionOptions.unshift({
		key: "All",
		name: translation("All"),
		description: "",
		value: "",
		children: (
			<div
				className={`center-text order-type-selection-radio-option typography-body-01 ${
					orderTypeOption === ""
						? "order-type-selection-radio-option-selected"
						: ""
				}`}
			>
				{translation("All")}
			</div>
		),
	});
	const orderTypeSelection: React.ReactNode = (
		<div
			style={{
				width: "100%",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={orderTypeSelectionOptions}
				selectedOption={orderTypeOption}
				onChange={(e) => {
					setOrderTypeOption(e.target.value);
				}}
				className="order-type-selection-radio-group"
			/>
		</div>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(OrderTableSortColumn).map((value) => {
					return {
						value: value,
						title: getOrderSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
				backgroundColor="transparent"
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getOrders(searchText);
	}, [currentPagination, sortBy, sortOrder, statusFilterOption]);

	useEffect(() => {
		setIsInAdminView(
			checkUserTypeMeetsRequirement(
				props.user?.role as UserType,
				UserType.ADMIN
			)
		);
	}, [props.user]);

	function gotoOrderDetailsPage(order: FrontendProductOrder) {
		setSelectedOrder(order);
		setCurrentPageIndex(1);
	}
	function goBackToViewOrdersPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedOrder(undefined);
		if (reload) {
			getOrders(searchText);
		}
	}
	function goToEditOrderPage(order: FrontendProductOrder) {
		setSelectedOrder(order);
		setCurrentPageIndex(2);
	}
	function goToOrderReportPage(order: FrontendProductOrder) {
		setSelectedOrder(order);
		setCurrentPageIndex(3);
	}

	const viewOrderTitle: React.ReactNode = (
		<div className="view-orders-page-header">
			<div className="typography-heading-01">{translation("ClientOrders")}</div>
			<CustomSearchBar
				id="view-order-page-searchbar"
				searchText={searchText}
				setSearchText={setSearchText}
				placeholder={translation("SearchByOrderNumber")}
				searchButtonText={translation("Search")}
				onSearch={getOrders}
			></CustomSearchBar>
		</div>
	);

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						minHeight: "90vh",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<div className="order-history-action-bar">
							{orderTypeSelection}
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "baseline",
									width: "100%",
								}}
							>
								{statusSelection}
								{sortOptionSelection}
							</div>
						</div>
						{loading ? (
							<OrderHistoryPageSkeletonUI />
						) : (
							<AdminClientOrderContent
								orders={orders}
								user={props.user}
								isInAdminView={isInAdminView}
								// sortBy={sortBy}
								// setSortBy={setSortBy}
								// sortOrder={sortOrder}
								// setSortOrder={setSortOrder}
								deleteOrder={deleteOrder}
								viewOrderCOC={viewOrderCOC}
								goToViewOrderDetails={gotoOrderDetailsPage}
								goToOrderReportPage={goToOrderReportPage}
								goToEditOrderPage={goToEditOrderPage}
								setDiscardOrderConfirmOpen={setDiscardOrderConfirmOpen}
								setProceedAction={setProceedAction}
							/>
						)}
					</div>
					{!loading && orders.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_900}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<OrderDetailsContent
					order={selectedOrder as FrontendProductOrder}
					isInAdminView={isInAdminView}
					itemList={(selectedOrder as FrontendProductOrder).itemList}
					user={props.user}
					goToEditOrderPage={goToEditOrderPage}
					goBackToViewOrdersPage={goBackToViewOrdersPage}
					setDiscardOrderConfirmOpen={setDiscardOrderConfirmOpen}
					goToOrderReportPage={goToOrderReportPage}
					viewOrderCOC={viewOrderCOC}
					setProceedAction={setProceedAction}
					refreshSelectedOrder={refreshSelectedOrder}
					discardUnstartedOrder={deleteOrder}
				></OrderDetailsContent>
			);
			break;
		case 2:
			currentComponent = (
				<NewOrEditOrder
					user={props.user}
					isInAdminView={true}
					company={props.company}
					order={selectedOrder}
					orderItemList={selectedOrder?.itemList}
					gotoViewOrderPage={() => goBackToViewOrdersPage(true)}
					setUnsavedChanges={setUnsavedChanges}
					goBackToOrderDetailsPage={() => {
						setCurrentPageIndex(1);
					}}
				></NewOrEditOrder>
			);
			break;
		case 3:
			currentComponent = (
				<AdminViewOrderReportPage
					selectedOrder={selectedOrder as FrontendProductOrder}
					goBackToViewOrdersPage={goBackToViewOrdersPage}
					gotoOrderDetailsPage={() =>
						gotoOrderDetailsPage(selectedOrder as FrontendProductOrder)
					}
				></AdminViewOrderReportPage>
			);
	}

	return (
		<div>
			{currentComponent}

			<DiscardOrderConfirmModal
				open={discardOrderConfirmOpen}
				setOpen={setDiscardOrderConfirmOpen}
				proceedAction={proceedAction}
			></DiscardOrderConfirmModal>
		</div>
	);
}
